import { useCallback } from "react";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import type { ActionSuggestionKind } from "./context/action-suggestion";
import {
  actionAiChipClickProps,
  actionAiOpenProps,
  actionAiQuerySubmitProps,
  actionCollapseProps,
  actionHistoryDayProps,
  actionHistoryEntryProps,
  actionHistoryViewProps,
  actionNoteProps,
  actionNotesSearchProps,
  actionSuggestionProps,
  actionTabSwitchProps,
  actionToolProps,
  type ActionAiEntry,
  type ActionHistoryView,
  type ActionNoteSource,
  type ActionToolGroup,
} from "./action-analytics";
import type { ActionTab } from "./action-tab";

export function useActionAnalytics(teenId: string | null) {
  const { track } = useTrackEvent();

  const trackTabSwitch = useCallback(
    (from: ActionTab, to: ActionTab) =>
      track(PostHogEvents.ACTION_TAB_SWITCH, actionTabSwitchProps(from, to, teenId)),
    [track, teenId],
  );

  const trackCollapseToggle = useCallback(
    (collapsed: boolean) =>
      track(
        PostHogEvents.ACTION_COLLAPSE_TOGGLE,
        actionCollapseProps(collapsed, teenId),
      ),
    [track, teenId],
  );

  const trackSuggestionView = useCallback(
    (kind: ActionSuggestionKind | null) =>
      track(
        PostHogEvents.ACTION_SUGGESTION_VIEW,
        actionSuggestionProps(kind, teenId),
      ),
    [track, teenId],
  );

  const trackSuggestionAccept = useCallback(
    (kind: ActionSuggestionKind | null) =>
      track(
        PostHogEvents.ACTION_SUGGESTION_ACCEPT,
        actionSuggestionProps(kind, teenId),
      ),
    [track, teenId],
  );

  const trackSuggestionDismiss = useCallback(
    (kind: ActionSuggestionKind | null) =>
      track(
        PostHogEvents.ACTION_SUGGESTION_DISMISS,
        actionSuggestionProps(kind, teenId),
      ),
    [track, teenId],
  );

  const trackNoteAdd = useCallback(
    (source: ActionNoteSource) =>
      track(PostHogEvents.ACTION_NOTE_ADD, actionNoteProps(source, teenId)),
    [track, teenId],
  );

  const trackNoteEdit = useCallback(
    (source: ActionNoteSource) =>
      track(PostHogEvents.ACTION_NOTE_EDIT, actionNoteProps(source, teenId)),
    [track, teenId],
  );

  const trackNoteDelete = useCallback(
    (source: ActionNoteSource) =>
      track(PostHogEvents.ACTION_NOTE_DELETE, actionNoteProps(source, teenId)),
    [track, teenId],
  );

  const trackNotesSearch = useCallback(
    (resultCount: number) =>
      track(
        PostHogEvents.ACTION_NOTES_SEARCH,
        actionNotesSearchProps(resultCount, teenId),
      ),
    [track, teenId],
  );

  const trackToolSelect = useCallback(
    (group: ActionToolGroup, toolId: string) =>
      track(PostHogEvents.ACTION_TOOL_SELECT, actionToolProps(group, toolId, teenId)),
    [track, teenId],
  );

  const trackHistoryView = useCallback(
    (view: ActionHistoryView) =>
      track(
        PostHogEvents.ACTION_HISTORY_VIEW,
        actionHistoryViewProps(view, teenId),
      ),
    [track, teenId],
  );

  const trackHistoryEntrySelect = useCallback(
    (group: ActionToolGroup, toolId: string) =>
      track(
        PostHogEvents.ACTION_HISTORY_ENTRY_SELECT,
        actionHistoryEntryProps(group, toolId, teenId),
      ),
    [track, teenId],
  );

  const trackHistoryDaySelect = useCallback(
    () =>
      track(
        PostHogEvents.ACTION_HISTORY_DAY_SELECT,
        actionHistoryDayProps(teenId),
      ),
    [track, teenId],
  );

  const trackAiOpen = useCallback(
    (entry: ActionAiEntry) =>
      track(PostHogEvents.ACTION_AI_OPEN, actionAiOpenProps(entry, teenId)),
    [track, teenId],
  );

  const trackAiQuerySubmit = useCallback(
    (messageLength: number) =>
      track(
        PostHogEvents.ACTION_AI_QUERY_SUBMIT,
        actionAiQuerySubmitProps(messageLength, teenId),
      ),
    [track, teenId],
  );

  const trackAiChipClick = useCallback(
    (promptId: string) =>
      track(
        PostHogEvents.ACTION_AI_CHIP_CLICK,
        actionAiChipClickProps(promptId, teenId),
      ),
    [track, teenId],
  );

  return {
    trackTabSwitch,
    trackCollapseToggle,
    trackSuggestionView,
    trackSuggestionAccept,
    trackSuggestionDismiss,
    trackNoteAdd,
    trackNoteEdit,
    trackNoteDelete,
    trackNotesSearch,
    trackToolSelect,
    trackHistoryView,
    trackHistoryEntrySelect,
    trackHistoryDaySelect,
    trackAiOpen,
    trackAiQuerySubmit,
    trackAiChipClick,
  };
}

export type UseActionAnalyticsResult = ReturnType<typeof useActionAnalytics>;
